import { StyleSheet, css } from 'aphrodite';
import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { useCallPipeline } from '../call/useCallPipeline';
import type { CallEventName, CallState, IncomingCall } from '../call/types';

type LogEntry = {
	id: number;
	timestamp: string;
	event: CallEventName;
	payload: unknown;
};

const ALL_EVENTS: CallEventName[] = [
	'device:initializing',
	'device:registered',
	'device:unregistered',
	'device:error',
	'token:willExpire',
	'token:refreshed',
	'call:incoming',
	'call:dialing',
	'call:accepted',
	'call:rejected',
	'call:connected',
	'call:mute-changed',
	'call:reconnecting',
	'call:reconnected',
	'call:ended',
];

const MAX_LOG_ENTRIES = 200;

export const HarnessPage: FC = () => {
	const pipeline = useCallPipeline();
	const { state, muted, initDevice, dial, accept, reject, hangup, setMuted, subscribe, devEmit } = pipeline;

	const [log, setLog] = useState<LogEntry[]>([]);
	const [dialNumber, setDialNumber] = useState('+15555555555');
	const logIdRef = useRef(0);

	useEffect(() => {
		const unsubs = ALL_EVENTS.map(eventName =>
			subscribe(eventName, (payload: unknown) => {
				logIdRef.current += 1;
				const entry: LogEntry = {
					id: logIdRef.current,
					timestamp: new Date().toISOString().slice(11, 23),
					event: eventName,
					payload,
				};
				setLog(prev => [entry, ...prev].slice(0, MAX_LOG_ENTRIES));
			})
		);
		return () => unsubs.forEach(u => u());
	}, [subscribe]);

	const clearLog = useCallback(() => setLog([]), []);

	const simulateIncoming = useCallback(() => {
		const incoming: IncomingCall = {
			sid: `sim-${Date.now()}`,
			from: '+19195550142',
			autoAnswer: false,
		};
		devEmit('call:incoming', incoming);
	}, [devEmit]);

	const simulateTokenExpiring = useCallback(() => {
		devEmit('token:willExpire');
	}, [devEmit]);

	// Twilio emits `unregistered` on any signaling-websocket drop, including ones a
	// connected call survives. Firing it here is the only way to exercise that
	// without an actual network blip: state must stay `connected`, and drop to
	// `uninit` from every other state.
	const simulateSignalingDrop = useCallback(() => {
		devEmit('device:unregistered');
	}, [devEmit]);

	const isRinging = state.status === 'ringing';
	const isConnected = state.status === 'connected';
	const isDialingOrConnected = state.status === 'dialing' || state.status === 'connected';

	return (
		<div className={css(styles.root)}>
			<header className={css(styles.header)}>
				<div>
					<div className={css(styles.eyebrow)}>Focused View</div>
					<h1 className={css(styles.title)}>Call pipeline harness</h1>
				</div>
				<div className={css(styles.headerMeta)}>
					<code>state: {state.status}</code>
					{isConnected && <code>muted: {muted ? 'yes' : 'no'}</code>}
				</div>
			</header>

			<section className={css(styles.panels)}>
				<article className={css(styles.panel)}>
					<h2 className={css(styles.panelTitle)}>State</h2>
					<pre className={css(styles.code)}>{formatState(state)}</pre>
				</article>

				<article className={css(styles.panel)}>
					<h2 className={css(styles.panelTitle)}>Controls</h2>

					<div className={css(styles.controlRow)}>
						<button
							type='button'
							className={css(styles.primaryBtn)}
							onClick={() => {
								void initDevice();
							}}
						>
							Init device
						</button>
						<button type='button' className={css(styles.btn)} onClick={simulateIncoming}>
							Simulate incoming (dev)
						</button>
						<button type='button' className={css(styles.btn)} onClick={simulateTokenExpiring}>
							Simulate token expiring (dev)
						</button>
						<button type='button' className={css(styles.btn)} onClick={simulateSignalingDrop}>
							Simulate signaling drop (dev)
						</button>
					</div>

					<div className={css(styles.controlRow)}>
						<input
							className={css(styles.input)}
							value={dialNumber}
							onChange={e => setDialNumber(e.target.value)}
							placeholder='+15555555555'
						/>
						<button
							type='button'
							className={css(styles.btn)}
							onClick={() => {
								void dial(dialNumber);
							}}
						>
							Dial
						</button>
					</div>

					<div className={css(styles.controlRow)}>
						<button type='button' className={css(styles.btn)} disabled={!isRinging} onClick={accept}>
							Accept
						</button>
						<button type='button' className={css(styles.btn)} disabled={!isRinging} onClick={reject}>
							Reject
						</button>
						<button type='button' className={css(styles.btn)} disabled={!isDialingOrConnected} onClick={hangup}>
							Hang up
						</button>
						<button type='button' className={css(styles.btn)} disabled={!isConnected} onClick={() => setMuted(!muted)}>
							{muted ? 'Unmute' : 'Mute'}
						</button>
					</div>
				</article>

				<article className={css(styles.panel, styles.panelWide)}>
					<div className={css(styles.panelTitleRow)}>
						<h2 className={css(styles.panelTitle)}>Event log · {log.length}</h2>
						<button type='button' className={css(styles.btnGhost)} onClick={clearLog}>
							Clear
						</button>
					</div>
					<div className={css(styles.logScroll)}>
						{log.length === 0 && <div className={css(styles.logEmpty)}>No events yet</div>}
						{log.map(entry => (
							<div key={entry.id} className={css(styles.logRow)}>
								<span className={css(styles.logTimestamp)}>{entry.timestamp}</span>
								<span className={css(styles.logEvent)}>{entry.event}</span>
								<span className={css(styles.logPayload)}>{formatPayload(entry.payload)}</span>
							</div>
						))}
					</div>
				</article>
			</section>
		</div>
	);
};

function formatState(state: CallState): string {
	return JSON.stringify(state, null, 2);
}

function formatPayload(payload: unknown): string {
	if (payload === undefined) return '';
	try {
		return JSON.stringify(payload);
	} catch {
		return String(payload);
	}
}

const styles = StyleSheet.create({
	root: {
		minHeight: '100vh',
		padding: '32px 40px 48px',
		background: '#FAFAF9',
		color: '#1C1B19',
		fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
	},
	header: {
		display: 'flex',
		alignItems: 'flex-start',
		justifyContent: 'space-between',
		gap: 24,
		marginBottom: 24,
	},
	eyebrow: {
		fontSize: 11,
		fontWeight: 600,
		letterSpacing: '0.08em',
		textTransform: 'uppercase',
		color: '#9A9995',
	},
	title: {
		margin: '4px 0 0',
		fontSize: 24,
		fontWeight: 600,
		letterSpacing: '-0.01em',
	},
	headerMeta: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'flex-end',
		gap: 4,
		fontFamily: "'JetBrains Mono', ui-monospace, monospace",
		fontSize: 12,
		color: '#6B6A66',
	},
	panels: {
		display: 'grid',
		gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
		gap: 16,
	},
	panel: {
		background: '#FFFFFF',
		border: '1px solid #E7E5E1',
		borderRadius: 12,
		padding: 16,
	},
	panelWide: {
		gridColumn: '1 / -1',
	},
	panelTitleRow: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		marginBottom: 10,
	},
	panelTitle: {
		margin: '0 0 10px',
		fontSize: 11,
		fontWeight: 600,
		letterSpacing: '0.08em',
		textTransform: 'uppercase',
		color: '#9A9995',
	},
	code: {
		margin: 0,
		fontFamily: "'JetBrains Mono', ui-monospace, monospace",
		fontSize: 12,
		lineHeight: 1.5,
		background: '#F4F3F1',
		borderRadius: 8,
		padding: 12,
		overflow: 'auto',
	},
	controlRow: {
		display: 'flex',
		flexWrap: 'wrap',
		gap: 8,
		marginBottom: 10,
		':last-child': {
			marginBottom: 0,
		},
	},
	btn: {
		appearance: 'none',
		height: 32,
		padding: '0 12px',
		borderRadius: 8,
		border: '1px solid #E7E5E1',
		background: '#FFFFFF',
		color: '#1C1B19',
		fontSize: 13,
		fontWeight: 500,
		cursor: 'pointer',
		fontFamily: 'inherit',
		transition: 'background 120ms',
		':hover': { background: '#F1EFEB' },
		':disabled': { opacity: 0.4, cursor: 'not-allowed' },
	},
	btnGhost: {
		appearance: 'none',
		height: 26,
		padding: '0 10px',
		borderRadius: 6,
		border: 'none',
		background: 'transparent',
		color: '#6B6A66',
		fontSize: 12,
		cursor: 'pointer',
		fontFamily: 'inherit',
		':hover': { background: '#F1EFEB', color: '#1C1B19' },
	},
	primaryBtn: {
		appearance: 'none',
		height: 32,
		padding: '0 14px',
		borderRadius: 8,
		border: 'none',
		background: '#1C1B19',
		color: '#FAFAF9',
		fontSize: 13,
		fontWeight: 500,
		cursor: 'pointer',
		fontFamily: 'inherit',
		':hover': { opacity: 0.92 },
	},
	input: {
		flex: '1 1 180px',
		height: 32,
		padding: '0 10px',
		borderRadius: 8,
		border: '1px solid #E7E5E1',
		background: '#FFFFFF',
		color: '#1C1B19',
		fontSize: 13,
		fontFamily: "'JetBrains Mono', ui-monospace, monospace",
		':focus': { outline: 'none', borderColor: '#9A9995' },
	},
	logScroll: {
		maxHeight: 360,
		overflow: 'auto',
		background: '#F4F3F1',
		borderRadius: 8,
		padding: 8,
		fontFamily: "'JetBrains Mono', ui-monospace, monospace",
		fontSize: 12,
		lineHeight: 1.6,
	},
	logEmpty: {
		padding: 12,
		color: '#9A9995',
		textAlign: 'center',
	},
	logRow: {
		display: 'grid',
		gridTemplateColumns: '96px 180px 1fr',
		gap: 10,
		padding: '2px 6px',
		borderRadius: 4,
		':hover': { background: '#E7E5E1' },
	},
	logTimestamp: {
		color: '#9A9995',
	},
	logEvent: {
		color: '#1C1B19',
		fontWeight: 500,
	},
	logPayload: {
		color: '#6B6A66',
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
	},
});
